import { Await, generatePath } from "react-router";
import {
    CollegeRelationships,
    CollegeResult,
    ConferenceResult,
    DivisionResult,
    RosterResult,
    SportResult,
    ProgramResult,
    MajorResult,
} from "~/lib/api/ecosystemV2.server";
import { SportListItem } from "~/lib/api/ecosystemV1.server";
import CollegePageHeader from "~/components/organisms/CollegePageHeader";
import PageContainer from "~/components/templates/PageContainer";
import { StatisticsBox } from "~/components/molecules/StatisticsBox";
import trophy from "~/assets/trophy.svg";
import mortarboard from "~/assets/mortarboard.svg";
import clipboard from "~/assets/clipboard.svg";
import lightbulb from "~/assets/lightbulb.svg";
import fb from "~/assets/facebook-circle.svg";
import ig from "~/assets/instagram-circle.svg";
import twitter from "~/assets/twitter-circle.svg";
import calendar from "~/assets/calendar.svg";
import crowd from "~/assets/crowd.svg";
import money from "~/assets/money-circle.svg";
import link from "~/assets/link.svg";
import * as format from "~/business/college/format";
import { percentage } from "~/lib/format";
import { RouteIds, RoutePaths, useRouteData } from "~/lib/routing";
import LeadFormCTA from "~/components/organisms/LeadFormCTA";
import { CardSection } from "~/components/atoms/CardSection";
import _ from "lodash";
import {
    ProgramRoster,
    ProgramRosterError,
    ProgramRosterLoading,
} from "~/components/molecules/ProgramRoster";
import {
    ProgramStaff,
    ProgramStaffError,
    ProgramStaffLoading,
} from "~/components/molecules/ProgramStaff";
import { CardSectionRow } from "~/components/atoms/CardSectionRow";
import { JoinButton } from "~/components/molecules/JoinButton";
import {
    StatisticsSection,
    StatisticsSectionError,
    StatisticsSectionLoading,
} from "~/components/molecules/StatisticsSection";
import { ProgressBar } from "~/components/atoms/ProgressBar";
import Image from "~/components/atoms/Image";
import { RefObject, Suspense, useRef } from "react";
import { useScrollPosition } from "~/lib/scroll";
import { cn } from "~/lib/utils";
import { Breadcrumb } from "~/components/molecules/Breadcrumbs";
import { CollegeMajors } from "~/components/molecules/CollegeMajors";
import AuthStickyNav from "~/components/organisms/AuthStickyNav";

export type CollegeData = {
    coaches?: ReturnType<CollegeRelationships["coaches"]>;
    college: CollegeResult;
    conference?: ConferenceResult;
    division?: DivisionResult;
    favorited: boolean;
    majors: MajorResult[];
    program?: ProgramResult;
    rosterData?: ReturnType<CollegeRelationships["roster"]>;
    sport: SportListItem;
    sports: SportResult[];
    authenticated: boolean;
    isBeta: boolean;
};

type NavRef = RefObject<HTMLDivElement | null>;

function useIsHighlighted(current: NavRef, next?: NavRef): boolean {
    useScrollPosition();
    const threshold = 16;
    const a = current.current?.getBoundingClientRect().top;
    const b = next?.current?.getBoundingClientRect().top;
    if (a === undefined) return false;
    if (b === undefined) {
        return a < threshold;
    } else {
        return a < threshold && b > threshold;
    }
}

function Sidebar({
    athletics,
    academics,
    admissions,
    cost,
    students,
}: {
    athletics: NavRef;
    academics: NavRef;
    admissions: NavRef;
    cost: NavRef;
    students: NavRef;
}) {
    function Li({ current, next, title }: { current: NavRef; next?: NavRef; title: string }) {
        const isHighlighted = useIsHighlighted(current, next);
        return (
            <li>
                <button
                    className={cn("flex gap-2", isHighlighted ? "font-body-bold" : "ml-4")}
                    onClick={() => current.current?.scrollIntoView({ behavior: "smooth" })}
                >
                    {isHighlighted && <span>{"\u25B8"}</span>}
                    {title}
                </button>
            </li>
        );
    }
    return (
        <div className="hidden w-[120px] min-w-[120px] md:block">
            <nav
                className="font-body sticky top-4 text-gray-80"
                aria-label="College Profile Sidebar"
            >
                <ul className="flex flex-col gap-6">
                    <Li current={athletics} next={academics} title="Athletics" />
                    <Li current={academics} next={admissions} title="Academics" />
                    <Li current={admissions} next={cost} title="Admission" />
                    <Li current={cost} next={students} title="Cost" />
                    <Li current={students} title="Students" />
                </ul>
            </nav>
        </div>
    );
}

export function Overview({
    division,
    conference,
    college,
    program,
}: Pick<CollegeData, "division" | "conference" | "college" | "program">) {
    const items = [];
    if (division)
        items.push({
            title: "Division",
            content: format.shortName(division.attrs.display_name),
        });
    if (conference) items.push({ title: "Conference", content: conference.attrs.abbreviation });
    items.push({ title: "Mascot", content: college.attrs.mascot });
    function Link({ href, label }: { href?: string | null; label: string }) {
        if (href === undefined || href === null) return null;
        return (
            <a
                href={href}
                target="_blank"
                className="font-body flex items-center gap-3 text-blue-80"
            >
                <Image src={link} alt="hyperlink" />
                {label}
            </a>
        );
    }
    return (
        <CardSection title="Overview">
            <div className="flex flex-col gap-4 px-6" data-testid="overview-section">
                <div className="sm:px-8">
                    <StatisticsBox
                        asset={{
                            path: trophy,
                            alt: "trophy",
                        }}
                        items={items}
                    />
                </div>
                {(college.attrs.website || program?.attrs.website) && (
                    <div className="flex flex-col items-start gap-2">
                        <Link href={college.attrs.website} label="School Website" />
                        <Link href={program?.attrs.website} label="Sports Program Website" />
                    </div>
                )}
            </div>
        </CardSection>
    );
}

const ROSTER_INSIGHTS_TITLE = "Roster Insights";
export function RosterStats({ roster }: { roster?: RosterResult }) {
    if (roster === undefined) return null;
    return (
        <StatisticsSection
            title={ROSTER_INSIGHTS_TITLE}
            data-testid="roster-insight-section"
            subtitle={`${roster.attrs.year} Roster Breakdown`}
            items={[
                {
                    title: "Freshmen",
                    content: roster.attrs.breakdown.freshman.toLocaleString(),
                },
                {
                    title: "Sophomore",
                    content: roster.attrs.breakdown.sophomore.toLocaleString(),
                },
                {
                    title: "Junior",
                    content: roster.attrs.breakdown.junior.toLocaleString(),
                },
                {
                    title: "Senior",
                    content: roster.attrs.breakdown.senior.toLocaleString(),
                },
            ]}
        />
    );
}

export function Culture({ program }: Pick<CollegeData, "program">) {
    if (program === undefined) return null;
    const { instagram, x, facebook } = program.attrs.social_media;
    if (instagram === null && x === null && facebook === null) return null;
    return (
        <CardSection title="Team Culture">
            <div className="px-6" data-testid="team-culture-section">
                {instagram && (
                    <CardSectionRow
                        left="Instagram Profile"
                        right={
                            <a href={instagram} target="_blank">
                                <Image
                                    loading="lazy"
                                    src={ig}
                                    alt="Instagram logo"
                                    className="size-[25px]"
                                />
                            </a>
                        }
                    />
                )}
                {x && (
                    <CardSectionRow
                        left="X Profile"
                        right={
                            <a href={x} target="_blank">
                                <Image
                                    loading="lazy"
                                    src={twitter}
                                    alt="Twitter logo"
                                    className="size-[25px]"
                                />
                            </a>
                        }
                    />
                )}
                {facebook && (
                    <CardSectionRow
                        left="Facebook Profile"
                        right={
                            <a href={facebook} target="_blank">
                                <Image
                                    loading="lazy"
                                    src={fb}
                                    alt="Facebook logo"
                                    className="size-[25px]"
                                />
                            </a>
                        }
                    />
                )}
            </div>
        </CardSection>
    );
}

function Scholarships({ division, program }: Pick<CollegeData, "program" | "division">) {
    const title = division
        ? `Athletic Scholarships at ${format.shortName(division.attrs.display_name)} Colleges`
        : "Athletic Scholarships";
    return (
        <CardSection title={title}>
            <div
                className="flex flex-col items-center gap-6 px-6 lg:flex-row"
                data-testid="athletic-scholarships-section"
            >
                <div className="w-full grow">
                    <CardSectionRow
                        left="Athletic Scholarships Offered?"
                        right={program?.attrs.offers_scholarships ? "Yes" : "No"}
                    />
                    <CardSectionRow
                        left="Scholarship Limit on Team"
                        right={program?.attrs.scholarship_limit?.toLocaleString() ?? "N/A"}
                    />
                </div>
                <div className="flex flex-col items-center gap-4 rounded bg-blue-20 px-12 py-4 lg:max-w-72">
                    <Image loading="lazy" src={lightbulb} alt="lightbulb" className="size-[53px]" />
                    <p className="font-metadata-medium text-center">
                        The best way to learn about a program’s scholarship availability is to
                        communicate with their coach.
                    </p>
                    <JoinButton size="lg" data-testid="scholarships" />
                </div>
            </div>
        </CardSection>
    );
}

export function Sports({ college, sports }: Pick<CollegeData, "college" | "sports">) {
    function sortGender(a: string, b: string) {
        const order = ["womens", "mens", "mixed"];
        return order.indexOf(a) - order.indexOf(b);
    }
    const byGender = _.groupBy(sports, s => s.attrs.gender);
    return (
        <CardSection title={`All Sports at ${college.attrs.name}`}>
            <div
                className="font-body flex flex-col gap-4 px-6 py-4 text-gray-80"
                data-testid="all-sports-section"
            >
                {Object.keys(byGender)
                    .sort(sortGender)
                    .map(gender => (
                        <div key={gender}>
                            <div className="font-subheader-1">{format.gender(gender)}</div>
                            {format.sports(byGender[gender])}
                        </div>
                    ))}
            </div>
        </CardSection>
    );
}

function Academics({ college }: Pick<CollegeData, "college">) {
    const { student_faculty_ratio, graduation_rate } = college.attrs;
    return (
        <StatisticsSection
            title="Academic Statistics"
            asset={{
                path: mortarboard,
                alt: "mortarboard",
                lazyLoad: true,
            }}
            items={[
                {
                    title: "Student-To-Faculty Ratio",
                    content: student_faculty_ratio ? `${student_faculty_ratio}-1` : null,
                },
                {
                    title: "4-Year Grad Rate",
                    content: percentage(graduation_rate),
                },
            ]}
        />
    );
}

function Admissions({ college }: Pick<CollegeData, "college">) {
    return (
        <StatisticsSection
            title="Admission Statistics"
            data-testid="admission-statistics-section"
            asset={{
                path: clipboard,
                alt: "clipboard",
                lazyLoad: true,
            }}
            items={[
                {
                    title: "Acceptance Rate",
                    content: percentage(college.attrs.acceptance_rate),
                },
                { title: "SAT Range", content: college.attrs.sat_range },
                { title: "ACT Range", content: college.attrs.act_range },
            ]}
        />
    );
}

function Deadlines({ college }: Pick<CollegeData, "college">) {
    return (
        <StatisticsSection
            title="Deadlines & Requirements"
            data-testid="deadlines-requirements-section"
            asset={{ path: calendar, alt: "calendar", lazyLoad: true }}
            items={[
                {
                    title: "Application Deadline",
                    content: college.attrs.application_deadline,
                },
                {
                    title: "Early Action Deadline",
                    content: college.attrs.early_action_deadline,
                },
            ]}
        />
    );
}

function Tuition({ college }: Pick<CollegeData, "college">) {
    function format(value?: number | null): string | null {
        if (value === null || value === undefined || value === 0) return null;
        return `$${value.toLocaleString()}/year`;
    }
    const price = format(college.attrs.net_price);
    const aid = format(college.attrs.average_total_aid_awarded);
    const students = percentage(college.attrs.students_receiving_financial_aid);
    const statistics = StatisticsBox({
        asset: { path: money, alt: "dollar sign", lazyLoad: true },
        items: [
            {
                title: "In-State Tuition",
                content: format(college.attrs.in_state_tuition),
            },
            {
                title: "Out-of-State Tuition",
                content: format(college.attrs.out_of_state_tuition),
            },
        ],
    });
    const details = price || aid || students;
    if (statistics === null && !details) return null;
    return (
        <CardSection title="Cost">
            <div className="flex flex-col gap-4 px-6 text-gray-80" data-testid="tuition-section">
                <div className="sm:px-8">{statistics}</div>
                {details && (
                    <div>
                        {price && (
                            <CardSectionRow
                                left="Net Price"
                                tooltip="Average cost after financial aid for students receiving grant or scholarship aid, as reported by the college."
                                right={price}
                            />
                        )}
                        {aid && <CardSectionRow left="Average Total Aid Award" right={aid} />}
                        {students && (
                            <CardSectionRow
                                left="Students Receiving Financial Aid"
                                right={students}
                            />
                        )}
                    </div>
                )}
            </div>
        </CardSection>
    );
}

export function Students({ college }: Pick<CollegeData, "college">) {
    const diversity: [string, number | null | undefined][] = [
        ["African American", college.attrs.diversity_breakdown_african_american],
        ["Asian", college.attrs.diversity_breakdown_asian],
        ["Hispanic", college.attrs.diversity_breakdown_hispanic],
        ["International", college.attrs.diversity_breakdown_international],
        ["Multiracial", college.attrs.diversity_breakdown_multiracial],
        ["Unknown", college.attrs.diversity_breakdown_unknown],
        ["White", college.attrs.diversity_breakdown_white],
    ];
    const breakdowns = _.chain(diversity)
        .filter(
            (tuple): tuple is [string, number] => !(tuple[1] === null || tuple[1] === undefined),
        )
        .filter(([, breakdown]) => breakdown > 0)
        .orderBy(([, breakdown]) => breakdown, "desc")
        .value();
    function Ratios({ college }: Pick<CollegeData, "college">) {
        const female = percentage(college.attrs.female_undergrad_ratio);
        const male = percentage(college.attrs.male_undergrad_ratio);
        if (female === null && male === null) return null;
        return (
            <div className="flex-1">
                {female && <CardSectionRow left="Female" right={female} />}
                {male && <CardSectionRow left="Male" right={male} />}
            </div>
        );
    }
    const ratios = Ratios({ college });
    return (
        <CardSection title="Students">
            <div className="flex flex-col px-6 py-2" data-testid="students-section">
                <div className="flex items-center gap-6">
                    <div className={cn("flex-1", ratios === null && "sm:px-8")}>
                        <StatisticsBox
                            asset={{ path: crowd, alt: "crowd", lazyLoad: true }}
                            items={[
                                {
                                    title: "Full Time Enrollment (Undergrad)",
                                    content: college.attrs.enrollment?.toLocaleString() ?? null,
                                },
                            ]}
                        />
                    </div>
                    {ratios}
                </div>
                {breakdowns.length > 0 && (
                    <div className="flex flex-col gap-1">
                        <span className="font-subheader-1 pb-2 pt-8 text-gray-80">
                            Diversity Breakdown
                        </span>
                        {breakdowns.map(([label, value]) => (
                            <ProgressBar key={label} label={label} value={value} />
                        ))}
                    </div>
                )}
            </div>
        </CardSection>
    );
}

export default function CollegePage() {
    const {
        college,
        sport,
        division,
        conference,
        rosterData,
        coaches,
        sports,
        program,
        majors,
        authenticated,
    } = useRouteData(RouteIds.COLLEGE_PROFILE);
    const { favoritedColleges } = useRouteData(RouteIds.USER_DATA);
    const favorited = favoritedColleges
        .map(college => college.attrs.ecosystem_id)
        .includes(college.id as string);
    const athletics = useRef<HTMLDivElement | null>(null);
    const academics = useRef<HTMLDivElement | null>(null);
    const admissions = useRef<HTMLDivElement | null>(null);
    const cost = useRef<HTMLDivElement | null>(null);
    const students = useRef<HTMLDivElement | null>(null);

    const Content = () => (
        <div className="flex w-full flex-col gap-6" data-testid="college-page">
            <div ref={athletics}>
                <Overview
                    division={division}
                    conference={conference}
                    college={college}
                    program={program}
                />
            </div>
            <Suspense fallback={<ProgramStaffLoading />}>
                <Await resolve={coaches} errorElement={<ProgramStaffError />}>
                    {resolvedCoaches => (
                        <ProgramStaff
                            name={college.attrs.name}
                            coaches={resolvedCoaches}
                            authenticated={authenticated}
                        />
                    )}
                </Await>
            </Suspense>

            <Suspense
                fallback={
                    <>
                        <ProgramRosterLoading />
                        <StatisticsSectionLoading title={ROSTER_INSIGHTS_TITLE} />
                    </>
                }
            >
                <Await
                    resolve={rosterData}
                    errorElement={
                        <>
                            <ProgramRosterError />
                            <StatisticsSectionError title={ROSTER_INSIGHTS_TITLE} />
                        </>
                    }
                >
                    {resolvedRosterData => (
                        <>
                            <ProgramRoster players={resolvedRosterData?.players} />
                            <RosterStats roster={resolvedRosterData?.roster} />
                        </>
                    )}
                </Await>
            </Suspense>

            <LeadFormCTA data-testid="mobile" className="lg:hidden" />
            <Culture program={program} />
            <Scholarships division={division} program={program} />
            <Sports college={college} sports={sports} />
            <div ref={academics} data-testid="academic-statistics-section">
                <Academics college={college} />
            </div>
            <LeadFormCTA data-testid="mobile" className="lg:hidden" />
            <CollegeMajors majors={majors} />
            <div ref={admissions}>
                <Admissions college={college} />
            </div>
            <Deadlines college={college} />
            <div ref={cost}>
                <Tuition college={college} />
            </div>
            <div ref={students}>
                <Students college={college} />
            </div>
        </div>
    );
    const breadcrumbs: Breadcrumb[] = [
        {
            label: "College Recruiting",
            href: "https://plus.imgacademy.com/college-recruiting",
        },
        {
            label: `${sport.name} Colleges`,
            href: generatePath(RoutePaths.COLLEGE_SEARCH, { sportSlug: sport.slug }),
        },
        {
            label: `${college.attrs.name}`,
        },
    ];
    return (
        <PageContainer className="bg-blue-10" breadcrumbs={breadcrumbs}>
            <CollegePageHeader
                college={college}
                division={division}
                conference={conference}
                favorited={favorited}
                sport={sport}
            />
            <div className="flex justify-center">
                <div className="flex w-full max-w-7xl gap-6 py-6 sm:px-6">
                    <Sidebar
                        athletics={athletics}
                        academics={academics}
                        admissions={admissions}
                        cost={cost}
                        students={students}
                    />
                    <Content />
                    <div className="hidden w-64 shrink-0 lg:block">
                        <LeadFormCTA data-testid="desktop" />
                    </div>
                </div>
            </div>
            <AuthStickyNav />
        </PageContainer>
    );
}
